import * as ToggleMenuFlyout from '@ori-events/toggle-menu-flyout';
import { styled } from '@ori-ui/mui';
import { useToken } from '@ori/auth';
import type { GlobalCustomersCustomerInfoDto } from '@ori/global-api-client/esm/models';
import { shouldForwardProp } from '@ori/presentation-utils';
import throttle from 'lodash.throttle';
import type { ReactPortal, VFC } from 'react';
import { memo, useEffect, useLayoutEffect, useState, lazy, useRef } from 'react';
import ReactDOM from 'react-dom';

import { AboutMenu } from '~/components/About/AboutMenu';
import { AccountFlyout } from '~/components/Account/AccountFlyout';
import { BurgerMenu } from '~/components/BurgerMenu/BurgerMenu';
import { JoinMenu } from '~/components/Join/JoinMenu';
import { LeftActionsSection } from '~/components/LeftActionsSection';
import { OriflameLogo } from '~/components/OriflameLogo';
import { RightActionsSection } from '~/components/RightActionsSection';
import { SearchResults } from '~/components/SearchResults/SearchResults';
import {
  CONSULTANT_NUMBER_QUERY_PARAM_KEY,
  DESKTOP_BREAKPOINT,
  RETURN_URL_QUERY_PARAM_KEY,
  SCROLL_THROTTLE_DELAY,
} from '~/constants';
import { useApiProviderContext } from '~/contexts/ApiProviderContext';
import { useAppConfigurationContext } from '~/contexts/AppConfigurationContext';
import { useNavigationContext } from '~/contexts/NavigationContext';
import { dispatchToggleMenu } from '~/helpers';
import { useClickSignInButtonSupportCenter, useMobileWidth } from '~/hooks';
import { log, setTopNavHeight, TopNavHeight } from '~/utils';

const LazyPersonalMenu = lazy(async () => ({
  default: (await import('@ori/personal-menu')).PersonalMenu,
}));

const InnerTopAreaRoot = styled('nav', {
  shouldForwardProp: shouldForwardProp(),
})(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.common.white,
  borderBottom: `2px solid ${theme.palette.grey[100]}`,
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateColumns: '1fr 100px 1fr',
  height: 'var(--top-nav-height)',
  left: 0,
  padding: theme.spacing(0, 3),
  position: 'fixed',
  top: 0,
  transition: 'height var(--top-nav-height-animation), background-color 0.1s ease-in-out',
  width: '100vw',
  zIndex: theme.zIndex.appBar,

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    gridTemplateColumns: '1fr 180px 1fr',
    height: 'var(--top-nav-height)',
    padding: theme.spacing(0, 9, 0, 6),
  },

  [theme.breakpoints.up('lg')]: {
    padding: theme.spacing(0, 11, 0, 8),
  },

  [theme.breakpoints.up('xl')]: {
    padding: theme.spacing(0, 15, 0, 12),
  },
}));

const InnerTopArea: VFC = memo(() => {
  const { globalApiClient } = useApiProviderContext();
  const { currentLanguage } = useAppConfigurationContext();
  const { customerId, token, formattedToken, isAnonymousUser } = useToken();
  const myPagesPersonalMenuRootRef = useRef<HTMLElement | null>();
  const [language, setLanguage] = useState(currentLanguage);

  const [consultantNumber, setConsultantNumber] =
    useState<GlobalCustomersCustomerInfoDto['consultantNumber']>();
  const [customerType, setCustomerType] =
    useState<GlobalCustomersCustomerInfoDto['customerType']>();
  const {
    externalLoginName,
    globalApiUrl,
    staticApiUrl,
    tenant,
    personalMenuInitialData,
    mobileOfficeApiUrl,
    consultantNumber: configConsultantNumber,
    consultantType,
    reportsItemName,
  } = useAppConfigurationContext();

  const isMobile = useMobileWidth();
  let personalMenu: ReactPortal | undefined;

  const { areNavigationsElementsFetched, getNavigationElements, toggleMenu, activeMenu } =
    useNavigationContext();

  useClickSignInButtonSupportCenter(activeMenu);

  // Previous implementation reacted to document language
  // TODO -- Is this really necessary? currentLanguage from config should be enough
  useEffect(() => {
    const documentLang = document.documentElement.lang;

    setLanguage((prevLanguage) => {
      if (documentLang && prevLanguage !== documentLang) {
        log.info(
          `A document lang found. Changing language for personal menu and burger menu from '${prevLanguage}' to '${documentLang}'`,
        );

        return documentLang;
      }

      if (!documentLang && currentLanguage !== prevLanguage) {
        log.info(
          `New currentLanguage value. Changing language for personal menu and burger menu from '${prevLanguage}' to '${currentLanguage}'`,
        );

        return currentLanguage;
      }

      return prevLanguage;
    });
  }, [currentLanguage]);

  // Find and save element
  useEffect(() => {
    myPagesPersonalMenuRootRef.current = document.getElementById('personal-menu-root');

    return () => {
      myPagesPersonalMenuRootRef.current = undefined;
    };
  }, []);

  useLayoutEffect(() => {
    const bodyEl = document.body;
    const topAreaRoot = document.getElementById('online-top-area-root');
    topAreaRoot?.classList.add('top-nav--is-loaded');
    bodyEl.classList.add(TopNavHeight.DESKTOP_DEFAULT);
  }, []);

  useLayoutEffect(() => {
    setTopNavHeight(isMobile ? TopNavHeight.MOBILE : TopNavHeight.DESKTOP_DEFAULT);
  }, [isMobile]);

  useEffect(() => {
    const handleScroll = () => {
      const isPageScrolled = window.scrollY > 0;

      if (!isMobile) {
        if (isPageScrolled) {
          setTopNavHeight(TopNavHeight.DESKTOP_SHRUNK);
        } else {
          setTopNavHeight(TopNavHeight.DESKTOP_DEFAULT);
        }
      }
    };
    const throttledHandleScroll = throttle(handleScroll, SCROLL_THROTTLE_DELAY);

    window.addEventListener('scroll', throttledHandleScroll);

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll);
    };
  }, [isMobile]);

  useEffect(() => {
    const toggleMenuHandler = (event: ToggleMenuFlyout.Event) => {
      toggleMenu(event.detail.menuType);
    };

    window.addEventListener(ToggleMenuFlyout.Name, toggleMenuHandler as EventListener);

    return () => {
      window.removeEventListener(ToggleMenuFlyout.Name, toggleMenuHandler as EventListener);
    };
  }, [toggleMenu]);

  useEffect(() => {
    if (!areNavigationsElementsFetched()) {
      getNavigationElements();
    }
  }, [areNavigationsElementsFetched, getNavigationElements]);

  useEffect(() => {
    const { searchParams } = new URL(window.location.href);
    const returnUrl = searchParams.get(RETURN_URL_QUERY_PARAM_KEY);
    const consultantNumberQueryParam = searchParams.get(CONSULTANT_NUMBER_QUERY_PARAM_KEY);

    if (returnUrl || externalLoginName || consultantNumberQueryParam) {
      dispatchToggleMenu(ToggleMenuFlyout.MenuTypes.AccountSideMenu);
    }
  }, [externalLoginName]);

  useEffect(() => {
    if (!isAnonymousUser && consultantNumber == null) {
      globalApiClient
        .getApplicationsNavigationCustomerInfo(tenant, customerId.toString())
        .then(
          ({ consultantNumber: consultantNumberResponse, customerType: customerTypeResponse }) => {
            setConsultantNumber(consultantNumberResponse);
            setCustomerType(customerTypeResponse);
          },
        )

        // eslint-disable-next-line no-console -- Logging errors to console.
        .catch((error: unknown) => console.warn(error));
    }
  }, [
    consultantNumber,
    customerId,
    globalApiUrl,
    tenant,
    globalApiClient,
    formattedToken,
    isAnonymousUser,
  ]);

  if (myPagesPersonalMenuRootRef.current && !isMobile) {
    personalMenu = ReactDOM.createPortal(
      <LazyPersonalMenu
        configuration={{
          consultantNumber: consultantNumber?.toString() ?? configConsultantNumber,
          customerId,
          globalApiUrl,
          lang: language,
          personalMenuInitialData,
          staticApiUrl,
          tenant,
          token,
          mobileOfficeApiUrl,
          consultantType,
          reportsItemName,
        }}
      />,
      myPagesPersonalMenuRootRef.current,
    );
  }

  return (
    <InnerTopAreaRoot>
      <LeftActionsSection />
      <OriflameLogo />
      <RightActionsSection />
      <BurgerMenu
        consultantNumber={consultantNumber}
        customerType={customerType}
        lang={language}
      />
      <AccountFlyout />
      <SearchResults formattedToken={formattedToken} />
      <AboutMenu />
      <JoinMenu />
      {personalMenu}
    </InnerTopAreaRoot>
  );
});
export const TopArea = InnerTopArea;
